import { DocumentActions } from '@/components/DocumentActions'
import {
  Button,
  Card,
  CardContent,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useCreditNotes, useDecideWriteOff } from '@/api/hooks'
import { SendDocumentIcon } from '@/components/SendDocument'
import { WriteOffDialog } from './WriteOffDialog'
import { useAuthStore } from '@/auth/authStore'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatMoney,
  humanise,
  StatusChip,
} from '@/components/common'

const STATUSES = ['REQUESTED', 'ISSUED', 'REJECTED']

/**
 * Balances the business has been asked to let go.
 *
 * <p>A customer settles 22,000 against 23,580 and the last 1,580 is never
 * coming. Somebody has to agree to stop chasing it, and that agreement is the
 * point of this screen: a balance that disappears from a report with nobody's
 * name against it is the thing an audit asks about.</p>
 */
export function WriteOffsTab() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const [status, setStatus] = useState('REQUESTED')
  const { data, isLoading, error } = useCreditNotes(status || undefined)
  const decide = useDecideWriteOff()
  const [raising, setRaising] = useState(false)

  return (
    <>
      <ErrorBanner error={error ?? decide.error} />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            sx={{ minWidth: 220 }}
            helperText="Requested ones are waiting on somebody"
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {value.charAt(0) + value.slice(1).toLowerCase()}
              </MenuItem>
            ))}
          </TextField>
          {canWrite('billing') ? (
            <Button variant="contained" onClick={() => setRaising(true)}>
              Write off a balance
            </Button>
          ) : null}
          </Stack>
        </CardContent>
      </Card>

      <WriteOffDialog open={raising} onClose={() => setRaising(false)} />

      <Card>
        {isLoading && <LinearProgress />}
        {data && data.length === 0 ? (
          <EmptyState message="Nothing waiting to be written off." />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Voucher</TableCell>
                <TableCell>Raised</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>What</TableCell>
                <TableCell>Why</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Off the balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(data ?? []).map((note) => (
                <TableRow key={note.id} hover>
                  <TableCell>{note.creditNoteNumber}</TableCell>
                  <TableCell>{formatDate(note.noteDate)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{note.customerName ?? '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {note.orderNumber ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell>{humanise(note.noteType)}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2">{note.reason}</Typography>
                    {note.remarks ? (
                      <Typography variant="caption" color="text.secondary">
                        {note.remarks}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">{formatMoney(note.taxableAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(note.taxAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(note.totalAmount)}</TableCell>
                  <TableCell>
                    <StatusChip status={note.status} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {/* Printable at any stage: the voucher is what the person
                          approving it signs, and it says on its face whether
                          the balance has actually come down. */}
                      <DocumentActions
                        path={`/api/v1/credit-notes/${note.id}/pdf`}
                        fileName={`${note.creditNoteNumber.replace(/\//g, '-')}.pdf`}
                        label="voucher"
                      />
                      <SendDocumentIcon kind="write-off-voucher" id={note.id} />
                      {note.status === 'REQUESTED' && can('writeoff.approve') ? (
                        <>
                          <Button
                            size="small"
                            color="error"
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({
                                id: note.id,
                                body: { approved: false, remarks: 'Not agreed; keep chasing it' },
                              })
                            }
                          >
                            Decline
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({
                                id: note.id,
                                body: { approved: true, remarks: 'Agreed' },
                              })
                            }
                          >
                            Approve
                          </Button>
                        </>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </>
  )
}
